import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { asyncActions } from "./actions.js";
import { fallbackActionId } from "./constants.js";
import { asyncActionSelector } from "./selectors.js";
import { RequestStatus } from "./types.js";
const useAsyncActionState = (action, defaultActionId) => {
  const actionType = action?.request(null)?.type;
  const actionState = useSelector((state) => asyncActionSelector(state, actionType))?.[defaultActionId ?? fallbackActionId] ?? { requestStatus: RequestStatus.None };
  const dispatch = useDispatch();
  const resetRequestStatus = () => {
    dispatch(
      asyncActions.setRequestStatus({
        actionType,
        actionId: defaultActionId,
        requestStatus: RequestStatus.None
      })
    );
  };
  const resetErrorMessage = () => {
    dispatch(
      asyncActions.setErrorMessage({
        actionType,
        actionId: defaultActionId,
        errorMessage: null,
        errorMessageValues: null
      })
    );
  };
  const resetAsyncActionState = () => {
    resetRequestStatus();
    resetErrorMessage();
  };
  return {
    ...actionState,
    resetAsyncActionState
  };
};
function useAsyncAction({
  asyncAction,
  onSuccess,
  onError
}) {
  const dispatch = useDispatch();
  const actionType = asyncAction.request(null).type;
  const asyncActionReduxState = useSelector((state) => asyncActionSelector(state, actionType));
  const idsInReduxState = Object.keys(asyncActionReduxState);
  const [prevStatuses, setPrevStatuses] = useState({});
  function handlePrevStatusesUpdate(id, status) {
    setPrevStatuses((prevState) => {
      return {
        ...prevState,
        [id]: status
      };
    });
  }
  function resetActionState(id = fallbackActionId) {
    handlePrevStatusesUpdate(id, RequestStatus.None);
    dispatch(
      asyncActions.setRequestStatus({
        actionType,
        actionId: id,
        requestStatus: RequestStatus.None
      })
    );
    dispatch(
      asyncActions.setErrorMessage({
        actionType,
        actionId: id,
        errorMessage: null,
        errorMessageValues: null
      })
    );
  }
  function dispatchAction(id = fallbackActionId, value) {
    dispatch(asyncAction.request(value, { actionId: id }));
  }
  useEffect(() => {
    idsInReduxState.forEach((id) => {
      const state = asyncActionReduxState[id];
      const prevRequestStatus = prevStatuses[id];
      if (state?.requestStatus !== prevRequestStatus) {
        if (state?.isNone && prevRequestStatus === RequestStatus.Progress) {
          if (onSuccess) {
            onSuccess(id, state.successfulResponse);
            resetActionState(id);
          }
        }
        if (state?.isError && prevRequestStatus === RequestStatus.Progress) {
          if (onError) {
            onError(id, asyncActionReduxState[id]?.errorMessage, asyncActionReduxState[id]?.errorMessageValues);
            resetActionState(id);
          }
        }
        handlePrevStatusesUpdate(id, state.requestStatus);
      }
    });
  }, [
    idsInReduxState.join(),
    idsInReduxState.map((id) => asyncActionReduxState[id]?.requestStatus).join(),
    asyncActionReduxState,
    resetActionState,
    handlePrevStatusesUpdate
  ]);
  useEffect(() => {
    return () => {
      dispatch(asyncActions.unuseAsyncAction({ actionType }));
    };
  }, []);
  return {
    idActionState: asyncActionReduxState,
    noIdActionState: asyncActionReduxState[fallbackActionId],
    resetActionState,
    dispatchAction
  };
}
export {
  useAsyncAction,
  useAsyncActionState
};
//# sourceMappingURL=hooks.js.map
